import React, {
  useState,
  useEffect,
} from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useChat } from '../context/ChatContext';
import { useGroupCall } from '../context/GroupCallContext';
import { api, ws } from '../lib/api';

import { WorkspaceRail } from '../components/layout/WorkspaceRail';
import { Sidebar } from '../components/layout/Sidebar';
import { AppHeader } from '../components/layout/AppHeader';
import { ChatArea } from '../components/chat/ChatArea';
import { ThreadDrawer } from '../components/chat/ThreadDrawer';

import { DmHub } from '../components/dms/DmHub';
import { ActivityView } from '../components/activity/ActivityView';
import { FilesView } from '../components/files/FilesView';
import { MeetingsView } from '../components/meetings/MeetingsView';

import { CreateWorkspaceModal } from '../components/modals/CreateWorkspaceModal';
import { CreateChannelModal } from '../components/modals/CreateChannelModal';
import { CreateGroupModal } from '../components/modals/CreateGroupModal';
import { NewDmModal } from '../components/modals/NewDmModal';
import { InviteModal } from '../components/modals/InviteModal';
import { SettingsModal } from '../components/modals/SettingsModal';
import { UserProfileModal } from '../components/modals/UserProfileModal';

import {
  Building2,
  Sparkles,
} from 'lucide-react';

export type AppView =
  | 'chat'
  | 'dms'
  | 'activity'
  | 'files'
  | 'meetings';

export const AppPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useAuth();

  const {
    currentWorkspace,
    workspaces,
    isLoading: isWorkspaceLoading,
  } = useWorkspace();

  const { joinGroupCall } = useGroupCall();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const joinMeetingId = searchParams.get('joinMeetingId');
    if (joinMeetingId && currentWorkspace) {
      (async () => {
        try {
          if (ws.getState() !== 'CONNECTED') {
            await ws.connect();
          }
          const meeting = await api.meetings.getDetails(currentWorkspace.id, joinMeetingId);
          if (meeting.status === 'SCHEDULED') {
            if (meeting.host.id === user?.id) {
              await api.meetings.start(currentWorkspace.id, meeting.id);
            } else {
              alert('Meeting has not been started by the host yet. Please wait for the host to start.');
              return;
            }
          }
          await joinGroupCall({
            workspaceId: currentWorkspace.id,
            channelId: joinMeetingId,
            callType: 'VIDEO',
          });
        } catch (err: any) {
          console.error('Failed to auto-join meeting:', err);
        }
      })();
    }
  }, [location.search, currentWorkspace, joinGroupCall, user]);

  const {
    activeThread,
    channels,
    directConversations,
    selectChannel,
    selectDm,
  } = useChat();

  const [activeView, setActiveView] =
    useState<AppView>('chat');

  /**
   * Sync URL sub-routes with the active application view.
   *
   * Supported routes:
   *
   * /app
   * /app/dms
   * /app/dms/:conversationId
   * /app/activity
   * /app/files
   * /app/channel/:channelId
   */
  useEffect(() => {
    const path = location.pathname;

    // ---------------------------------------------------------------
    // Direct Messages
    // ---------------------------------------------------------------
    if (path.startsWith('/app/dms')) {
      const parts =
        path.split('/app/dms/');

      if (parts[1]) {
        const convId = parts[1];

        const conv =
          directConversations.find(
            (conversation) =>
              conversation.id ===
              convId
          );

        if (conv) {
          selectDm(conv);
        }
      }

      setActiveView('dms');
      return;
    }

    // ---------------------------------------------------------------
    // Activity  (/app/activity or /workspace/:id/activity)
    // ---------------------------------------------------------------
    if (
      path.startsWith('/app/activity') ||
      /\/activity(\/?$|\/)/.test(path)
    ) {
      setActiveView('activity');
      return;
    }

    // ---------------------------------------------------------------
    // Files  (/app/files or /workspace/:id/files)
    // ---------------------------------------------------------------
    if (
      path.startsWith('/app/files') ||
      /\/files(\/?$|\/)/.test(path)
    ) {
      setActiveView('files');
      return;
    }

    // ---------------------------------------------------------------
    // Meetings  (/app/meetings or /workspace/:id/meetings)
    // ---------------------------------------------------------------
    if (
      path.startsWith('/app/meetings') ||
      /\/meetings(\/?$|\/)/.test(path)
    ) {
      setActiveView('meetings');
      return;
    }

    // ---------------------------------------------------------------
    // Channel
    // ---------------------------------------------------------------
    if (
      path.startsWith(
        '/app/channel/'
      )
    ) {
      const parts =
        path.split('/app/channel/');

      if (parts[1]) {
        const channelId = parts[1];

        const channel =
          channels.find(
            (item) =>
              item.id === channelId
          );

        if (channel) {
          selectChannel(channel);
        }
      }

      setActiveView('chat');
      return;
    }

    // ---------------------------------------------------------------
    // Default
    // ---------------------------------------------------------------
    setActiveView('chat');
  }, [
    location.pathname,
    directConversations,
    channels,
    selectDm,
    selectChannel,
  ]);

  /**
   * Change the main application view and keep
   * the URL synchronized with it.
   */
  const handleSelectView = (
    view: AppView
  ) => {
    setActiveView(view);

    if (view === 'dms') {
      navigate('/app/dms');
      return;
    }

    if (view === 'activity') {
      navigate('/app/activity');
      return;
    }

    if (view === 'files') {
      navigate('/app/files');
      return;
    }

    if (view === 'meetings') {
      navigate('/app/meetings');
      return;
    }

    navigate('/app');
  };

  // ------------------------------------------------------------------
  // Modal state
  // ------------------------------------------------------------------

  const [
    showCreateWorkspace,
    setShowCreateWorkspace,
  ] = useState(false);

  const [
    showCreateChannel,
    setShowCreateChannel,
  ] = useState(false);

  const [
    createChannelGroupId,
    setCreateChannelGroupId,
  ] = useState<
    string | undefined
  >(undefined);

  const [
    showCreateGroup,
    setShowCreateGroup,
  ] = useState(false);

  const [
    showNewDm,
    setShowNewDm,
  ] = useState(false);

  const [
    showInvite,
    setShowInvite,
  ] = useState(false);

  const [
    showSettings,
    setShowSettings,
  ] = useState(false);

  const [
    showProfile,
    setShowProfile,
  ] = useState(false);

  // ------------------------------------------------------------------
  // Authentication loading
  // ------------------------------------------------------------------

  if (isAuthLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />

          <span>
            Authenticating Sahaj Connect...
          </span>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Authentication guard
  // ------------------------------------------------------------------

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  // ------------------------------------------------------------------
  // Create channel handler
  // ------------------------------------------------------------------

  const handleOpenCreateChannel = (
    groupId?: string
  ) => {
    setCreateChannelGroupId(
      groupId
    );

    setShowCreateChannel(true);
  };

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-background text-foreground select-none">

      {/* ============================================================
          Far Left: Workspace Rail
          ============================================================ */}
      <WorkspaceRail
        onOpenCreateWorkspace={() =>
          setShowCreateWorkspace(true)
        }
        onOpenProfile={() =>
          setShowProfile(true)
        }
        onOpenSettings={() =>
          setShowSettings(true)
        }
      />

      {/* ============================================================
          Main Container
          ============================================================ */}
      {workspaces.length === 0 &&
      !isWorkspaceLoading ? (
        /*
         * Workspace Onboarding Empty State
         */
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-background">

          <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4 shadow-sm">
            <Building2 className="h-8 w-8" />
          </div>

          <h2 className="text-2xl font-bold tracking-tight mb-2">
            Create your first Workspace
          </h2>

          <p className="text-sm text-muted-foreground max-w-md mb-6 leading-relaxed">
            Workspaces are dedicated homes
            for your company or team to
            communicate across channels
            and direct messages.
          </p>

          <button
            type="button"
            onClick={() =>
              setShowCreateWorkspace(
                true
              )
            }
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-bold text-sm rounded-xl shadow-lg hover:bg-primary/90 transition hover:scale-105"
          >
            <Sparkles className="h-4 w-4" />

            <span>
              Create Workspace
            </span>
          </button>
        </div>
      ) : (
        <div className="flex-1 flex h-full min-w-0 overflow-hidden">

          {/* ========================================================
              Sidebar (Shown for channels & workspace — hidden in DM split workspace like Slack)
              ======================================================== */}
          {activeView !== 'dms' && (
            <Sidebar
              activeView={activeView}
              onSelectView={
                handleSelectView
              }
              onOpenCreateChannel={
                handleOpenCreateChannel
              }
              onOpenCreateGroup={() =>
                setShowCreateGroup(true)
              }
              onOpenNewDm={() =>
                setShowNewDm(true)
              }
              onOpenInvite={() =>
                setShowInvite(true)
              }
              onOpenSettings={() =>
                setShowSettings(true)
              }
              onOpenProfile={() =>
                setShowProfile(true)
              }
            />
          )}

          {/* ========================================================
              Center Main View Switcher
              ======================================================== */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

            {/* ------------------------------------------------------
                Direct Messages
                ------------------------------------------------------ */}
            {activeView === 'dms' ? (
              <DmHub
                onBack={() =>
                  handleSelectView(
                    'chat'
                  )
                }
              />
            ) : activeView ===
              'activity' ? (
              /* ----------------------------------------------------
                 Activity
                 ---------------------------------------------------- */
              <ActivityView
                onNavigateToContext={(
                  type
                ) => {
                  if (
                    type ===
                    'files'
                  ) {
                    handleSelectView(
                      'files'
                    );
                  } else {
                    handleSelectView(
                      'chat'
                    );
                  }
                }}
              />
            ) : activeView ===
              'files' ? (
              /* ----------------------------------------------------
                 Files
                 ---------------------------------------------------- */
              <FilesView />
            ) : activeView ===
              'meetings' ? (
              /* ----------------------------------------------------
                 Meetings
                 ---------------------------------------------------- */
              <MeetingsView />
            ) : (
              /* ----------------------------------------------------
                 Chat View
                 ---------------------------------------------------- */
              <>
                <AppHeader />

                <div className="flex-1 flex min-w-0 overflow-hidden">
                  <ChatArea />

                  {activeThread && (
                    <ThreadDrawer />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ============================================================
          Interactive Modals
          ============================================================ */}

      <CreateWorkspaceModal
        isOpen={
          showCreateWorkspace
        }
        onClose={() =>
          setShowCreateWorkspace(
            false
          )
        }
      />

      <CreateChannelModal
        isOpen={
          showCreateChannel
        }
        onClose={() =>
          setShowCreateChannel(
            false
          )
        }
        defaultGroupId={
          createChannelGroupId
        }
      />

      <CreateGroupModal
        isOpen={showCreateGroup}
        onClose={() =>
          setShowCreateGroup(
            false
          )
        }
      />

      <NewDmModal
        isOpen={showNewDm}
        onClose={() =>
          setShowNewDm(false)
        }
      />

      <InviteModal
        isOpen={showInvite}
        onClose={() =>
          setShowInvite(false)
        }
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() =>
          setShowSettings(false)
        }
      />

      <UserProfileModal
        isOpen={showProfile}
        onClose={() =>
          setShowProfile(false)
        }
      />
    </div>
  );
};